import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRightLeft, Banknote, Calculator, ChevronRight, CreditCard, Landmark, Package, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ApiLink } from '../../../APILink/ApiLink';
import authHeader from '../../../Auth/authHeader';
import { errorAlert } from '../../../Components/Alerts';
import { FiscalYearContext } from '../../../FiscalYear/FiscalYearContext';
import './BankCashPanel.scss';

const formatCurrency = (value) => {
  const numericValue = Number(value) || 0;
  return `€${Math.abs(numericValue).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default function BankCashPanel() {
  const [todaySales, setTodaySales] = useState(0);
  const [arkaTotal, setArkaTotal] = useState(0);
  const [banksTotal, setBanksTotal] = useState(0);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [supplierTotal, setSupplierTotal] = useState(0);
  const [vatTotal, setVatTotal] = useState(0);
  const [stokuTotal, setStokuTotal] = useState(0);
  const [malli, setMalli] = useState(0);
  const [stoku, setStoku] = useState(0);

  const { fiscalYear } = useContext(FiscalYearContext);

  useEffect(() => {
    const kmsh = Number(malli) - Number(stokuTotal);
    const currentStoku = Number(malli) - Number(kmsh);
    setStoku(currentStoku);
  }, [malli, stokuTotal]);

  useEffect(() => {
    const headers = authHeader();

    const fetchAllDetails = async () => {
      try {
        const response = await axios.get(ApiLink.API_URL + 'Dashboard/detailsTotal', {
          headers,
          params: { fiscalYear },
        });

        if (response.data && response.data.length > 0) {
          setTodaySales(response.data[0].TotalSalesToday || 0);
        }
      } catch (error) {
        errorAlert('Gabim në kërkesë:', error);
        if (error.response && error.response.data && error.response.data.errorMessage) {
          errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
        } else if (error.message) {
          errorAlert(`Gabim: ${error.message}`);
        } else {
          errorAlert('Gabim i panjohur në kërkesë. Shih konsolën për detaje.');
        }
      }
    };

    const fetchCompanyTotals = async () => {
      try {
        const response = await axios.get(ApiLink.API_URL + 'Dashboard/detailsForCompany', {
          headers,
          params: { fiscalYear },
        });

        if (response.data && response.data.length > 0) {
          setArkaTotal(response.data[0].Arkat || 0);
          setBanksTotal(response.data[0].Bankat || 0);
          setClientsTotal(response.data[0].Klientet || 0);
          setSupplierTotal(response.data[0].Furnitoret || 0);
          setVatTotal(response.data[0].TVSH || 0);
        }
      } catch (error) {
        errorAlert('Gabim në kërkesë:', error);
        if (error.response && error.response.data && error.response.data.errorMessage) {
          errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
        } else if (error.message) {
          errorAlert(`Gabim: ${error.message}`);
        } else {
          errorAlert('Gabim i panjohur në kërkesë. Shih konsolën për detaje.');
        }
      }
    };

    const fetchTotalAmount = async () => {
      try {
        const response = await axios.get(ApiLink.API_URL + 'AccountingBooks/TotaliStokut', {
          headers,
          params: { fiscalYear },
        });
        if (response.data && response.data.length > 0) {
          setStokuTotal(response.data[0].StokuTotal || 0);
        }
      } catch (error) {
        errorAlert('Gabim në kërkesë:', error);
        if (error.response && error.response.data && error.response.data.errorMessage) {
          errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
        } else if (error.message) {
          errorAlert(`Gabim: ${error.message}`);
        } else {
          errorAlert('Gabim i panjohur në kërkesë. Shih konsolën për detaje.');
        }
      }
    };

    const fetchMalliTotal = async () => {
      try {
        const response = await axios.get(ApiLink.API_URL + 'FinanceStatement/malliTotal', {
          headers,
          params: { fiscalYear },
        });
        if (response.data && response.data.length > 0) {
          setMalli(response.data[0].Malli || 0);
        }
      } catch (error) {
        errorAlert('Gabim në kërkesë:', error);
        if (error.response && error.response.data && error.response.data.errorMessage) {
          errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
        } else if (error.message) {
          errorAlert(`Gabim: ${error.message}`);
        } else {
          errorAlert('Gabim i panjohur në kërkesë. Shih konsolën për detaje.');
        }
      }
    };

    fetchAllDetails();
    fetchCompanyTotals();
    fetchTotalAmount();
    fetchMalliTotal();
  }, [fiscalYear]);

  const accounts = [
    { key: 'sales-day', name: 'Shitjet Ditore', value: todaySales, icon: ShoppingCart, tone: 'sales-day', to: '/Sales/salesindetails', isPrimary: true },
    { key: 'bank', name: 'Gjendja Bankave', value: banksTotal, icon: Landmark, tone: 'bank', to: '/Reports/moneybank' },
    { key: 'cash', name: 'Gjendja Arkave', value: arkaTotal, icon: Banknote, tone: 'cash', to: '/Reports/cashmoney' },
    { key: 'receivable', name: 'Klientet (LA)', value: clientsTotal, icon: ArrowRightLeft, tone: 'receivable', to: '/Reports/accountsreceivable' },
    { key: 'payable', name: 'Furnitoret (LP)', value: supplierTotal, icon: CreditCard, tone: 'payable', to: '/Reports/accountspayable' },
    { key: 'stock', name: 'Stoku', value: stoku, icon: Package, tone: 'stock', to: '/accounting/inventory' },
    { key: 'vat', name: 'TVSH', value: vatTotal, icon: Calculator, tone: vatTotal < 0 ? 'negative' : 'vat', to: '#' },
  ];

  return (
    <section className="nwd-bank-cash-panel">
      <div className="nwd-bank-cash-panel__header">
        <h2 className="nwd-bank-cash-panel__title">Bank & Cash</h2>
        <p className="nwd-bank-cash-panel__subtitle">Përmbledhje e llogarive financiare</p>
      </div>

      <div className="nwd-bank-cash-panel__list">
        {accounts.map((account) => {
          const isNegative = Number(account.value) < 0;
          const rowClassName = `nwd-bank-cash-panel__item${account.isPrimary ? ' nwd-bank-cash-panel__item--primary' : ''}`;

          return (
            <Link key={account.key} to={account.to} className={rowClassName}>
              <div className="nwd-bank-cash-panel__item-left">
                <div className={`nwd-bank-cash-panel__icon nwd-bank-cash-panel__icon--${account.tone}`}>
                  <account.icon />
                </div>
                <span className="nwd-bank-cash-panel__name">{account.name}</span>
              </div>

              <div className="nwd-bank-cash-panel__item-right">
                <span className={`nwd-bank-cash-panel__value ${isNegative ? 'is-negative' : ''}`}>
                  {isNegative ? '-' : ''}
                  {formatCurrency(account.value)}
                </span>
                <ChevronRight className="nwd-bank-cash-panel__chevron" />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}